import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { getVPH } from "@/lib/viewport";

// Scroll-driven "fill" text: words start dim grey and turn solid white as the
// visitor scrolls through the section. Fully responsive / wrapping.
export const TransitionSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const totalScroll = el.offsetHeight - getVPH();
        const scrolled = Math.min(Math.max(-rect.top, 0), totalScroll);
        setProgress(totalScroll > 0 ? scrolled / totalScroll : 0);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const groups = [
    {
      key: "lines",
      items: t.transition.lines,
      bold: false,
      cls: "space-y-2 text-lg leading-relaxed sm:text-2xl md:text-3xl",
    },
    {
      key: "now",
      items: t.transition.now,
      bold: false,
      cls: "mt-9 space-y-2 text-2xl leading-relaxed sm:text-3xl md:text-4xl",
    },
    {
      key: "closing",
      items: t.transition.closing,
      bold: true,
      cls: "mt-10 font-display text-4xl leading-[1.1] sm:text-5xl md:text-6xl",
    },
  ];

  let gi = 0;
  const totalWords = [
    ...t.transition.lines,
    ...t.transition.now,
    ...t.transition.closing,
  ]
    .join(" ")
    .split(/\s+/).length;
  const fillPos = progress * (totalWords + 6);

  const renderLine = (line, bold) => {
    const words = line.split(/\s+/);
    return words.map((w, i) => {
      const idx = gi++;
      const local = Math.min(Math.max(fillPos - idx, 0), 1);
      const alpha = 0.16 + 0.84 * local;
      return (
        <span key={i} style={{ color: `rgba(255,255,255,${alpha})` }}>
          {w}
          {i < words.length - 1 ? " " : ""}
        </span>
      );
    });
  };

  return (
    <section ref={ref} data-testid="transition-section" className="relative h-[calc(var(--vph)*3.6)]">
      <div className="sticky top-0 flex h-[var(--vph)] flex-col items-center justify-center overflow-hidden px-6 py-16 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          data-testid="transition-heading"
          className="mb-10 max-w-[15ch] text-balance text-center font-display text-3xl leading-tight text-amber-200 sm:mb-14 sm:max-w-2xl sm:text-4xl md:text-5xl"
        >
          {t.transition.heading}
        </motion.p>

        <div className="mx-auto w-full max-w-2xl text-center">
          {groups.map((g) => (
            <div key={g.key} className={g.cls}>
              {g.items.map((line, li) => (
                <p key={li} className={g.bold ? "font-extrabold" : ""}>
                  {renderLine(line, g.bold)}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
